import { useState, useEffect, useRef, useCallback } from "react";
import {
  X, Loader2, Save, Maximize2, Minimize2, Clock, StickyNote,
  Bookmark, BookmarkPlus, Trash2, PanelRightOpen, PanelRightClose,
  Plus, AlertTriangle, RotateCcw, CheckCircle2, WifiOff, RefreshCw
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Progress } from "../../components/ui/progress";
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from "../../components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import api from "../../lib/api";
import { toast } from "sonner";

const ScormPlayer = ({ course, enrollmentId, onClose, onProgressUpdate }) => {
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const cmiRef = useRef({});
  const saveTimerRef = useRef(null);
  const timeTimerRef = useRef(null);
  const onProgressUpdateRef = useRef(onProgressUpdate);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  // Keep ref current without causing re-renders
  useEffect(() => { onProgressUpdateRef.current = onProgressUpdate; }, [onProgressUpdate]);

  // Core state
  const [loading, setLoading] = useState(true);
  const [contentError, setContentError] = useState(false);
  const [apiReady, setApiReady] = useState(false);
  const [cmiData, setCmiData] = useState({});
  const [status, setStatus] = useState("not_started");
  const [progressPercent, setProgressPercent] = useState(0);

  // Sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarHint, setSidebarHint] = useState(false);
  const [sidebarTab, setSidebarTab] = useState("notes");
  const [notes, setNotes] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [newBookmarkLabel, setNewBookmarkLabel] = useState("");

  // Time & save
  const [timeSpent, setTimeSpent] = useState(0);
  const [totalTimeSpent, setTotalTimeSpent] = useState(0);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [savePulse, setSavePulse] = useState(false);

  // Fullscreen & resume
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [resumeLocation, setResumeLocation] = useState("");

  // SCORM init tracking
  const [scormInitialized, setScormInitialized] = useState(false);

  // ── Load saved data on mount ──
  useEffect(() => {
    const loadData = async () => {
      if (!enrollmentId) return;
      try {
        const [scormRes, notesRes] = await Promise.all([
          api.get(`/enrollments/${enrollmentId}/scorm-data`).catch(() => ({ data: {} })),
          api.get(`/enrollments/${enrollmentId}/notes`).catch(() => ({ data: { notes: [], bookmarks: [] } })),
        ]);
        const savedCmi = scormRes.data || {};
        cmiRef.current = savedCmi;
        setCmiData(savedCmi);
        setNotes(notesRes.data?.notes || []);
        setBookmarks(notesRes.data?.bookmarks || []);

        // Load progress state and total accumulated time
        try {
          const enrollRes = await api.get(`/enrollments/${enrollmentId}`);
          if (enrollRes.data?.progress_percent) {
            setProgressPercent(enrollRes.data.progress_percent);
          }
          if (enrollRes.data?.time_spent_seconds) {
            setTotalTimeSpent(enrollRes.data.time_spent_seconds);
          }
        } catch (e) { /* ignore */ }

        // Check for resume (fix #8)
        const savedLocation = savedCmi["cmi.core.lesson_location"] || savedCmi["cmi.location"] || "";
        const savedStatus = savedCmi["cmi.core.lesson_status"] || savedCmi["cmi.completion_status"] || "";
        if (savedLocation && savedStatus !== "completed" && savedStatus !== "passed") {
          setResumeLocation(savedLocation);
          setShowResumePrompt(true);
        }
      } catch (e) { /* ignore */ }
    };
    loadData();
  }, [enrollmentId]);

  // ── Time tracker (fix #7 - starts from accumulated) ──
  useEffect(() => {
    timeTimerRef.current = setInterval(() => setTimeSpent(t => t + 1), 1000);
    return () => { if (timeTimerRef.current) clearInterval(timeTimerRef.current); };
  }, []);

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m ${sec}s`;
  };

  // ── Save progress ──
  const saveProgress = useCallback(async () => {
    if (!enrollmentId || Object.keys(cmiRef.current).length === 0) return;
    try {
      await api.post(`/enrollments/${enrollmentId}/scorm-progress`, { cmi_data: cmiRef.current });
      setLastSavedAt(new Date());
      setSavePulse(true);
      setTimeout(() => setSavePulse(false), 2000);
      onProgressUpdateRef.current?.();
    } catch (e) { console.error("Save failed:", e); }
  }, [enrollmentId]);

  // ── Auto-save every 30s (stable - no cleanup save to prevent flicker) ──
  useEffect(() => {
    saveTimerRef.current = setInterval(() => saveProgress(), 30000);
    return () => {
      if (saveTimerRef.current) clearInterval(saveTimerRef.current);
    };
  }, [saveProgress]);

  // ── SCORM API Bridge: define full API on parent window, iframe accesses via window.parent.API ──
  useEffect(() => {
    const scormAPI12 = {
      LMSInitialize: () => {
        setScormInitialized(true);
        setLoading(false);
        setStatus("in_progress");
        return "true";
      },
      LMSFinish: () => { saveProgress(); return "true"; },
      LMSGetValue: (key) => cmiRef.current[key] || "",
      LMSSetValue: (key, value) => {
        cmiRef.current[key] = value;
        if (key === "cmi.core.lesson_status" || key === "cmi.completion_status") {
          if (value === "completed" || value === "passed") { setStatus("completed"); setProgressPercent(100); }
          else if (value === "incomplete") setStatus("in_progress");
          setCmiData({ ...cmiRef.current });
        } else if (key === "cmi.core.score.raw" || key === "cmi.score.raw") {
          try { setProgressPercent(Math.min(parseInt(value), 100)); } catch(e) {}
          setCmiData({ ...cmiRef.current });
        } else if (key === "cmi.core.lesson_location" || key === "cmi.location") {
          try {
            if (value && value.includes("/")) {
              const parts = value.split("/");
              const curr = parseInt(parts[0]);
              const total = parseInt(parts[1]);
              if (total > 0) setProgressPercent(Math.round((curr / total) * 100));
            }
          } catch(e) {}
          setCmiData({ ...cmiRef.current });
        }
        return "true";
      },
      LMSCommit: () => "true",
      LMSGetLastError: () => "0",
      LMSGetErrorString: () => "No error",
      LMSGetDiagnostic: () => "",
    };
    const scormAPI2004 = {
      Initialize: () => scormAPI12.LMSInitialize(),
      Terminate: () => scormAPI12.LMSFinish(),
      GetValue: (key) => scormAPI12.LMSGetValue(key),
      SetValue: (key, value) => scormAPI12.LMSSetValue(key, value),
      Commit: () => "true",
      GetLastError: () => "0",
      GetErrorString: () => "No error",
      GetDiagnostic: () => "",
    };
    window.API = scormAPI12;
    window.API_1484_11 = scormAPI2004;
    setApiReady(true);
    return () => { delete window.API; delete window.API_1484_11; };
  }, [saveProgress]);

  // ── Inject API into iframe contentWindow as backup for cross-origin edge cases ──
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const handleLoad = () => {
      try {
        const iw = iframe.contentWindow;
        if (iw) { iw.API = window.API; iw.API_1484_11 = window.API_1484_11; }
      } catch (e) { /* cross-origin — parent API will be found via traversal */ }
      setTimeout(() => setLoading(false), 3000);
    };
    const handleError = () => { setLoading(false); setContentError(true); };
    iframe.addEventListener("load", handleLoad);
    iframe.addEventListener("error", handleError);
    return () => {
      iframe.removeEventListener("load", handleLoad);
      iframe.removeEventListener("error", handleError);
    };
  }, []);

  // Fix #1: Additional timeout — if still loading after 15s, force error state
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, 15000);
    return () => clearTimeout(timeout);
  }, [loading]);

  // ── Fullscreen ──
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  // ── Fix #4: Escape key handler + Fix #15: Smarter copy protection ──
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Fix #4: Escape to exit
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
        return;
      }
      // Fix #15: Only block copy/paste OUTSIDE of notes area
      const isInNotesArea = e.target.closest('[data-notes-area]');
      if (isInNotesArea) return; // Allow normal keyboard in notes

      if ((e.ctrlKey || e.metaKey) && ["u", "U", "s", "S"].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (e.key === "F12") {
        e.preventDefault();
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Notes sidebar hint (fix #10) ──
  useEffect(() => {
    const hintShown = sessionStorage.getItem("scorm_sidebar_hint");
    if (!hintShown) {
      const timer = setTimeout(() => {
        setSidebarHint(true);
        sessionStorage.setItem("scorm_sidebar_hint", "1");
        setTimeout(() => setSidebarHint(false), 5000);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, []);

  // ── Notes CRUD ──
  const addNote = async () => {
    if (!newNote.trim() || !enrollmentId) return;
    try {
      const slideInfo = cmiRef.current["cmi.core.lesson_location"] || cmiRef.current["cmi.location"] || "";
      const res = await api.post(`/enrollments/${enrollmentId}/notes`, {
        text: newNote.trim(), slide_info: slideInfo,
      });
      setNotes(prev => [...prev, res.data]);
      setNewNote("");
      toast.success("Note saved");
    } catch (e) { toast.error("Failed to save note"); }
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/enrollments/${enrollmentId}/notes/${noteId}`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (e) { toast.error("Failed to delete"); }
  };

  // ── Bookmarks CRUD + Navigation (fix #9) ──
  const addBookmark = async () => {
    if (!enrollmentId) return;
    const slideInfo = cmiRef.current["cmi.core.lesson_location"] || cmiRef.current["cmi.location"] || "";
    const label = newBookmarkLabel.trim() || `Bookmark at ${slideInfo || "current position"}`;
    try {
      const res = await api.post(`/enrollments/${enrollmentId}/bookmarks`, {
        label, slide_info: slideInfo,
      });
      setBookmarks(prev => [...prev, res.data]);
      setNewBookmarkLabel("");
      toast.success("Bookmark added");
    } catch (e) { toast.error("Failed to bookmark"); }
  };

  const deleteBookmark = async (bookmarkId) => {
    try {
      await api.delete(`/enrollments/${enrollmentId}/bookmarks/${bookmarkId}`);
      setBookmarks(prev => prev.filter(b => b.id !== bookmarkId));
    } catch (e) { toast.error("Failed to delete"); }
  };

  // Fix #9: Navigate to bookmark by setting lesson_location and reloading iframe
  const navigateToBookmark = (bm) => {
    if (!bm.slide_info) {
      toast.info("No position data for this bookmark");
      return;
    }
    cmiRef.current["cmi.core.lesson_location"] = bm.slide_info;
    cmiRef.current["cmi.location"] = bm.slide_info;
    setCmiData({ ...cmiRef.current });
    if (iframeRef.current) {
      setLoading(true);
      iframeRef.current.src = iframeRef.current.src;
    }
    toast.info(`Navigating to: ${bm.label}`);
  };

  // ── Retry loading (fix #5) ──
  const retryLoad = () => {
    setContentError(false);
    setLoading(true);
    if (iframeRef.current) {
      iframeRef.current.src = iframeRef.current.src;
    }
  };

  // ── Close handler ──
  const handleClose = async () => {
    await saveProgress();
    toast.success("Progress saved");
    onClose();
  };

  // ── Resume handler ──
  const handleResumeChoice = (resumeFromSaved) => {
    setShowResumePrompt(false);
    if (!resumeFromSaved) {
      delete cmiRef.current["cmi.core.lesson_location"];
      delete cmiRef.current["cmi.location"];
      setCmiData({ ...cmiRef.current });
    }
  };

  // SCORM content URL — relative path ensures same-origin with parent (critical for window.parent.API)
  const scormUrl = `/api/courses/${course.id}/scorm-content/${course.scorm_entry_point || "index_lms.html"}`;
  const currentLocation = cmiData["cmi.core.lesson_location"] || cmiData["cmi.location"] || "";
  const lessonStatus = cmiData["cmi.core.lesson_status"] || cmiData["cmi.completion_status"] || "";

  // "Last saved" text (fix #11)
  const getLastSavedText = () => {
    if (!lastSavedAt) return null;
    const diffSecs = Math.floor((Date.now() - lastSavedAt.getTime()) / 1000);
    if (diffSecs < 5) return "Just saved";
    if (diffSecs < 60) return `Saved ${diffSecs}s ago`;
    return `Saved ${Math.floor(diffSecs / 60)}m ago`;
  };

  // Force re-render every 10s for "last saved" text
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(v => v + 1), 10000);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[100] bg-black flex flex-col"
      data-testid="scorm-player-overlay"
      tabIndex={0}
    >
      {/* Resume Prompt Dialog (fix #8) */}
      <Dialog open={showResumePrompt} onOpenChange={setShowResumePrompt}>
        <DialogContent className="max-w-sm bg-[#1a2332] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Resume Course?</DialogTitle>
            <DialogDescription className="text-white/60">
              You were previously at position: <span className="text-primary font-medium">{resumeLocation}</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="border-white/20 text-white/70 hover:bg-white/10" onClick={() => handleResumeChoice(false)} data-testid="start-fresh-btn">
              Start Fresh
            </Button>
            <Button className="bg-primary" onClick={() => handleResumeChoice(true)} data-testid="resume-btn">
              Resume Where I Left Off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Header Bar (responsive) ── */}
      <div className="flex items-center justify-between px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-[#0f172a] border-b border-white/10 shrink-0">
        <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
          {/* Logo */}
          <div className="flex items-center shrink-0 mr-1.5 sm:mr-3 pr-1.5 sm:pr-4 border-r border-white/10" data-testid="player-logo">
            <img src="/odesie-logo-white.png" alt="Odesie" className="h-7 sm:h-9 w-auto" />
          </div>
          <h3 className="text-[11px] sm:text-sm font-semibold text-white truncate max-w-[120px] sm:max-w-[350px]" data-testid="scorm-player-title">
            {course.title}
          </h3>
          <Badge variant="outline" className={`text-[9px] sm:text-[10px] shrink-0 border-white/20 ${
            status === "completed" ? "text-green-400 border-green-400/30" :
            status === "in_progress" ? "text-blue-400 border-blue-400/30" : "text-white/50"
          }`}>
            {status === "completed" ? "Completed" : status === "in_progress" ? "In Progress" : "Not Started"}
          </Badge>
        </div>

        <div className="flex items-center gap-0.5 sm:gap-1">
          {/* Progress bar */}
          <div className="hidden sm:flex items-center gap-2 mr-2 px-3 py-1 rounded-md bg-white/5">
            <div className="w-20">
              <Progress value={progressPercent} className="h-1.5 bg-white/10" />
            </div>
            <span className="text-[11px] font-medium text-white/70 w-8 text-right">{progressPercent}%</span>
          </div>

          {/* Time tracker */}
          <div className="hidden md:flex items-center gap-3 px-2 py-1 rounded-md bg-white/5 text-xs" data-testid="time-tracker">
            <div className="flex items-center gap-1.5 text-white/60">
              <Clock className="w-3 h-3" />
              <span>{formatTime(timeSpent)}</span>
            </div>
            <div className="w-px h-3 bg-white/20" />
            <div className="flex items-center gap-1 text-white/40" title="Total time spent on this course">
              <span className="text-[10px]">Total:</span>
              <span>{formatTime(totalTimeSpent + timeSpent)}</span>
            </div>
          </div>

          {/* Save */}
          <Button variant="ghost" size="sm" onClick={saveProgress} className={`text-xs h-7 sm:h-8 gap-1 px-2 sm:px-3 ${savePulse ? "text-green-400" : "text-white/70"} hover:text-white hover:bg-white/10`} data-testid="scorm-save-btn">
            {savePulse ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">{savePulse ? "Saved" : "Save"}</span>
          </Button>

          {/* Sidebar toggle */}
          <div className="relative">
            <Button variant="ghost" size="icon" onClick={() => { setSidebarOpen(!sidebarOpen); setSidebarHint(false); }} className="h-7 w-7 sm:h-8 sm:w-8 text-white/70 hover:text-white hover:bg-white/10" data-testid="toggle-sidebar-btn">
              {sidebarOpen ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
            </Button>
            {sidebarHint && !sidebarOpen && (
              <div className="absolute -bottom-10 right-0 bg-primary text-white text-[10px] px-2.5 py-1.5 rounded-md whitespace-nowrap animate-bounce shadow-lg z-50">
                Take notes & bookmark slides
                <div className="absolute -top-1 right-3 w-2 h-2 bg-primary rotate-45" />
              </div>
            )}
          </div>

          <Button variant="ghost" size="icon" onClick={toggleFullscreen} className="hidden sm:flex h-7 w-7 sm:h-8 sm:w-8 text-white/70 hover:text-white hover:bg-white/10" data-testid="fullscreen-btn">
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={handleClose} className="h-7 w-7 sm:h-8 sm:w-8 text-white/70 hover:text-white hover:bg-white/10" data-testid="scorm-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* ── Content Area (fix #16 - responsive) ── */}
      <div className="flex-1 flex min-h-0 flex-col sm:flex-row">
        {/* SCORM iframe */}
        <div className="flex-1 relative">
          {/* Loading overlay (fix #1 - better detection) */}
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#0f172a] z-30">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-3" />
                <p className="text-sm text-white/50">Loading course content...</p>
                <p className="text-[10px] text-white/30 mt-1">This may take a few moments</p>
              </div>
            </div>
          )}

          {/* Error overlay (fix #5) */}
          {contentError && !loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#0f172a] z-30">
              <div className="text-center max-w-sm px-6">
                <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                  <WifiOff className="w-8 h-8 text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Content Failed to Load</h3>
                <p className="text-sm text-white/50 mb-6">The SCORM content couldn't be loaded. This could be a network issue or the package may be corrupted.</p>
                <div className="flex gap-3 justify-center">
                  <Button variant="outline" className="border-white/20 text-white/70 hover:bg-white/10" onClick={handleClose}>
                    Exit
                  </Button>
                  <Button onClick={retryLoad} className="gap-2" data-testid="retry-load-btn">
                    <RefreshCw className="w-4 h-4" /> Retry
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* No SCORM content uploaded */}
          {!course.scorm_entry_point && !loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#0f172a] z-30">
              <div className="text-center max-w-sm px-6">
                <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-amber-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">No Content Uploaded</h3>
                <p className="text-sm text-white/50 mb-6">This SCORM course doesn't have content uploaded yet. An administrator needs to upload the SCORM package first.</p>
                <Button variant="outline" className="border-white/20 text-white/70 hover:bg-white/10" onClick={handleClose} data-testid="no-content-exit-btn">
                  Go Back
                </Button>
              </div>
            </div>
          )}

          {/* iframe — uses same-origin src for SCORM content (window.parent.API accessible) */}
          <iframe
            ref={iframeRef}
            src={apiReady && course.scorm_entry_point ? scormUrl : undefined}
            className="w-full h-full border-0 bg-white"
            title={course.title}
            allow="fullscreen"
            data-testid="scorm-iframe"
          />

          {/* Watermark Overlay (fix #12 - reduced intensity, fewer instances) */}
          <div className="absolute inset-0 pointer-events-none z-20 overflow-hidden select-none" data-testid="watermark-overlay">
            <div className="absolute inset-0 opacity-[0.025]">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="absolute text-base font-bold text-black whitespace-nowrap"
                  style={{
                    top: `${(i % 3) * 35 + 10}%`,
                    left: `${Math.floor(i / 3) * 50 + 5}%`,
                    transform: "rotate(-25deg)",
                    letterSpacing: "0.15em",
                  }}>
                  Odesie
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Interactive Sidebar ── */}
        {sidebarOpen && (
          <div className="w-full sm:w-80 border-t sm:border-t-0 sm:border-l border-white/10 bg-[#0f172a] flex flex-col shrink-0 max-h-[40vh] sm:max-h-none" data-testid="scorm-sidebar" data-notes-area="true">
            <Tabs value={sidebarTab} onValueChange={setSidebarTab} className="flex flex-col h-full">
              <TabsList className="grid grid-cols-2 mx-3 mt-3 bg-white/5">
                <TabsTrigger value="notes" className="text-xs data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/50">
                  <StickyNote className="w-3.5 h-3.5 mr-1" /> Notes ({notes.length})
                </TabsTrigger>
                <TabsTrigger value="bookmarks" className="text-xs data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/50">
                  <Bookmark className="w-3.5 h-3.5 mr-1" /> Pins ({bookmarks.length})
                </TabsTrigger>
              </TabsList>

              {/* Notes Tab */}
              <TabsContent value="notes" className="flex-1 flex flex-col mt-0 p-3 gap-3 overflow-hidden">
                <div className="flex gap-2" data-notes-area="true">
                  <Textarea
                    placeholder="Take a note... (Ctrl+Enter to save)"
                    value={newNote}
                    onChange={e => setNewNote(e.target.value)}
                    className="min-h-[60px] max-h-[100px] bg-white/5 border-white/10 text-white text-xs placeholder:text-white/30 resize-none"
                    onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) addNote(); }}
                    data-testid="note-input"
                    data-notes-area="true"
                  />
                </div>
                <Button size="sm" onClick={addNote} disabled={!newNote.trim()} className="w-full bg-blue-600 hover:bg-blue-700 text-xs h-8" data-testid="add-note-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Note
                </Button>

                <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                  {notes.length === 0 ? (
                    <div className="text-center py-6">
                      <StickyNote className="w-8 h-8 mx-auto text-white/10 mb-2" />
                      <p className="text-xs text-white/30">No notes yet</p>
                      <p className="text-[10px] text-white/20 mt-1">Cmd/Ctrl + Enter to save</p>
                    </div>
                  ) : (
                    notes.map(note => (
                      <div key={note.id} className="bg-white/5 rounded-lg p-3 group" data-testid={`note-${note.id}`}>
                        <p className="text-xs text-white/80 whitespace-pre-wrap break-words select-text">{note.text}</p>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-2">
                            {note.slide_info && (
                              <Badge variant="outline" className="text-[9px] border-white/10 text-white/30">
                                Slide {note.slide_info}
                              </Badge>
                            )}
                            <span className="text-[9px] text-white/20">
                              {new Date(note.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </div>
                          <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100 text-white/30 hover:text-red-400 hover:bg-transparent"
                            onClick={() => deleteNote(note.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>

              {/* Bookmarks Tab (fix #9 - clickable navigation) */}
              <TabsContent value="bookmarks" className="flex-1 flex flex-col mt-0 p-3 gap-3 overflow-hidden">
                <div className="flex gap-2">
                  <Input
                    placeholder="Bookmark label (optional)"
                    value={newBookmarkLabel}
                    onChange={e => setNewBookmarkLabel(e.target.value)}
                    className="bg-white/5 border-white/10 text-white text-xs placeholder:text-white/30 h-8"
                    onKeyDown={e => { if (e.key === "Enter") addBookmark(); }}
                    data-testid="bookmark-input"
                    data-notes-area="true"
                  />
                  <Button size="sm" onClick={addBookmark} className="bg-amber-600 hover:bg-amber-700 h-8 px-3 shrink-0" data-testid="add-bookmark-btn">
                    <BookmarkPlus className="w-3.5 h-3.5" />
                  </Button>
                </div>

                <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                  {bookmarks.length === 0 ? (
                    <div className="text-center py-6">
                      <Bookmark className="w-8 h-8 mx-auto text-white/10 mb-2" />
                      <p className="text-xs text-white/30">No bookmarks yet</p>
                      <p className="text-[10px] text-white/20 mt-1">Pin sections to jump back to them</p>
                    </div>
                  ) : (
                    bookmarks.map(bm => (
                      <div
                        key={bm.id}
                        className="flex items-center gap-2 bg-white/5 rounded-lg p-2.5 group cursor-pointer hover:bg-white/10 transition-colors"
                        onClick={() => navigateToBookmark(bm)}
                        data-testid={`bookmark-${bm.id}`}
                      >
                        <Bookmark className="w-4 h-4 text-amber-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-white/80 truncate">{bm.label}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {bm.slide_info && (
                              <span className="text-[9px] text-white/30">Slide {bm.slide_info}</span>
                            )}
                            <span className="text-[10px] text-amber-400/60">Click to jump</span>
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100 text-white/30 hover:text-red-400 hover:bg-transparent shrink-0"
                          onClick={(e) => { e.stopPropagation(); deleteBookmark(bm.id); }}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>

      {/* ── Bottom Status Bar (responsive) ── */}
      <div className="flex items-center justify-between px-2 sm:px-4 py-1.5 sm:py-2 bg-[#0f172a] border-t border-white/10 shrink-0">
        <div className="flex items-center gap-2 sm:gap-4 text-[10px] sm:text-xs text-white/50">
          <span>Status: <span className={
            status === "completed" ? "text-green-400 font-medium" :
            status === "in_progress" ? "text-blue-400 font-medium" : "text-white/40"
          }>{lessonStatus || status}</span></span>
          {/* Mobile progress */}
          <span className="sm:hidden flex items-center gap-1">
            <span className="text-white/70 font-medium">{progressPercent}%</span>
          </span>
          {cmiData["cmi.core.score.raw"] && (
            <span className="hidden sm:inline">Score: <span className="text-white/70 font-medium">{cmiData["cmi.core.score.raw"]}%</span></span>
          )}
          {currentLocation && (
            <span className="hidden md:inline">Position: <span className="text-white/70">{currentLocation}</span></span>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-white/40">
          {lastSavedAt && (
            <span className={`hidden sm:inline transition-colors ${savePulse ? "text-green-400" : ""}`}>
              {savePulse && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
              {getLastSavedText()}
            </span>
          )}
          {!lastSavedAt && <span className="hidden sm:inline">Auto-saves every 30s</span>}
          <span className="hidden md:inline">Esc to exit</span>
          <span className="text-white/25">Odesie</span>
        </div>
      </div>
    </div>
  );
};

export default ScormPlayer;
